import { Component, OnInit, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subject, from, of, Subscription } from "rxjs";
import { tap, mergeMap, map, finalize } from "rxjs/operators";
import esri = __esri;
import { BaseMapControlComponent } from "../base-map-control/base-map-control.component"; 
import { AreaOfReviewPoint } from "../../../gis/models/odnr-map-point.model";
import { GISConfig } from "../../../gis/models/gis-config.model";
import { GisLocationService, CoordinateSystem } from "../../../gis/services/gis-location.service";
import { GisQueryService } from "../../../gis/services/gis-query.service";
import { WellConfigType, ensureWellComponentConfig, StatePlane, WellConfigurationIndex, getContextLocation, WellDirection } from "../new-casing-plan/models/casing-plan.model";
import { DomainDefinitionService } from "../../../builder/services/domain-definition.service";
import { Domain } from "../../../builder/models/domain.model";
import { VIEW_WIDGETS } from "../../../gis/components/odnr-map/models/widgets.model";
import { RendererTypes } from "../../../builder/components/state/controls/config/data/well-related/map-control-data-config.component";
import { ActionRadius } from "../../../gis/components/odnr-map/renderer/areaOfReviewRenderer";
import { DocumentManagementService, IAzureBlobSasToken, IUploadFile } from "../../../common/services/document-management.service";
import { FlowButtonOverrideService } from "../../../flow/services/flow-button-override.service";
import { Renderer } from "../../../gis/components/odnr-map/renderer/renderer.interface";
import { WellType } from "../../../common/models/well.model";
import { ContextInteractionService } from "../../services/context-interaction.service";
import { TemplateService } from "../../../common/components/pdf-viewer/services/template.service";
import { genId } from "../../../core/services/genId";
import { AuthService } from "../../../services/auth.service";
import { Button } from "../../../flow/models/flow.model";

@Component({
    selector: "app-area-of-review-map",
    templateUrl: "./area-of-review-map.component.html",
    standalone: false
})
export class AreaOfReviewMapComponent extends BaseMapControlComponent implements OnInit, OnDestroy {
    static componentName = "AreaOfReviewMapComponent";

    FEET_PER_MILE: number = 5280;

    actionRadius: number;
    defaultZoom: number = 15;
    direction: string;
    formationDefinitions: Domain[];
    gisConfig: GISConfig;

    loadingPoints$ = new BehaviorSubject<boolean>(false);

    enhancedRecovery: WellType = { description: "EORP", type: "ER_R"  };
    saltwaterInjection: WellType = { description: "", type: "SW_R" };
    solutionMining: WellType = { description: "", type: "SM_R" };
    gasFluidType: Domain;
    reviewPoints: AreaOfReviewPoint[] = [];
    subscription: Subscription = new Subscription();
    wellPoints: AreaOfReviewPoint[] = [];
    wellTypeDefinitions: Domain[];
    wellTypeDescriptionDefinitions: Domain[];
    widgets = [
        { type: VIEW_WIDGETS.layerList, position: "top-right" }
    ];
    location: string = "files";

    get wellDirection(): string {
        if (this.isEdit) return "";
        return this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.WellDirection));
    }

    constructor(public readonly locationService: GisLocationService,
        public readonly queryService: GisQueryService,
        public readonly domainService: DomainDefinitionService,
        protected readonly buttonOverride: FlowButtonOverrideService,
        protected readonly docService: DocumentManagementService,
        public readonly contextService: ContextInteractionService,
        public readonly templateService: TemplateService,
        public readonly _authService: AuthService) {
        super(buttonOverride, docService, contextService);
        this.gisConfig = window["GIS.Conf"] as GISConfig;
    }

    ngOnInit() {
        super.ngOnInit();
        this.componentData.config.wellDataPoints = ensureWellComponentConfig(WellConfigType.AreaOfReviewMap, this.componentData.config.wellDataPoints);
        if (!this.isEdit) {
            this.loadingPoints$.next(true);
            if (this.componentContext) {
                this.storedLatitude = Number(this.getContext("latitude"));
                this.storedLongitude = Number(this.getContext("longitude"));
                const x = this.getLongitudeFromContext();
                const y = this.getLatitudeFromContext();
                let plane = this.getStatePlane();
                let latLong: number[];
                if (plane.length > 0) {
                    const coordinateSystem = (plane.toLowerCase() === StatePlane.South) ? CoordinateSystem.OHIO_SOUTH : CoordinateSystem.OHIO_NORTH;
                    latLong = this.locationService.statePlaneToWebMercator(x, y, coordinateSystem, true);
                } else {
                    latLong = [y, x];
                }               
                this.currentLatitude = latLong[0];
                this.currentLongitude = latLong[1];
                this.storeContext(this.currentLatitude, "latitude");
                this.storeContext(this.currentLongitude, "longitude");
            }
            this.componentData.config.widgets = this.widgets;
            this.componentData.config.properties = { zoom: this.defaultZoom, center: [this.currentLongitude, this.currentLatitude] };
            this.componentData.config.rendererType = RendererTypes.AreaOfReview;
           
            const formObs$ = this.domainService.getValues("FORMATION").pipe(tap((data) => {
                this.formationDefinitions = data;
            }));

            const wellTypObs$ = this.domainService.getValues("WELL_TYP").pipe(tap((data) => {
                this.wellTypeDefinitions = data;
            }));

            const wellTypeObs$ = this.domainService.getValues("WELL_TYPE").pipe(tap((data) => {
                this.wellTypeDefinitions = this.wellTypeDefinitions.concat(data);
            }));

            const descripObs$ = this.domainService.getValues("WLTYP_DESC").pipe(tap((data) => {
                this.wellTypeDescriptionDefinitions = data;
            }))

            const fluidObs$ = this.domainService.getValues("INJ_FLUID").pipe(tap((data) => {
                this.gasFluidType = data.find(f => f.Code.toUpperCase() === "GAS");
                this.actionRadius = this.getActionRadius();
                this.componentData.config.actionRadius = this.actionRadius;
            }));

            const dataObs$ = formObs$.pipe(
                mergeMap((data) => {
                    return wellTypObs$;
                }),
                mergeMap((data) => {
                    return wellTypeObs$;
                }),
                mergeMap((data) => {
                    return descripObs$;
                }),
                mergeMap((data) => {
                    return fluidObs$;
                }),
                mergeMap((data) => {
                    if (this.enableEditing) {
                        this.wellPoints.push({ latitude: this.currentLatitude, longitude: this.currentLongitude, type: "WellHead", wellName: "Proposed Well Head" } as AreaOfReviewPoint);
                        let gisObs$ = from(this.queryService.resolveBufferQueryAtPoint(this.gisConfig.existingWellsGISLayer, "Well_Pts", [this.currentLatitude, this.currentLongitude], (this.FEET_PER_MILE * this.actionRadius), "PointType = 'WellHead'"))
                            .pipe(map((result) => {
                                if (result && result.features) {
                                    for (let r = 0; r < result.features.length; r++) {
                                        const point = {
                                            APINumber: result.features[r].attributes["APINumber"],
                                            latitude: result.features[r].attributes["Lat83"],
                                            longitude: result.features[r].attributes["Long83"],
                                            orgId: result.features[r].attributes["OrgId"],
                                            orgName: result.features[r].attributes["OrgName"],
                                            producingFormation: this.getFormationName(result.features[r].attributes["ProposedFormation"]),
                                            totalDepth: (result.features[r].attributes["Depth"]) ? result.features[r].attributes["Depth"] : "Unknown",
                                            type: "existing",
                                            wellName: result.features[r].attributes["Name"],
                                            wellType: this.getWellType(result.features[r].attributes["WellType"]),
                                            wellTypeDescription: this.getWellTypeDescription((result.features[r].attributes["WellTypeDescription"]) ? result.features[r].attributes["WellTypeDescription"] : "")
                                        } as AreaOfReviewPoint;
                                        this.reviewPoints.push(point); //need to keep the points to review separate from the new well head, so the new well head doesn't end up on the export table
                                    }
                                }
                                if (this.componentData.contextTarget) {
                                    this.setContextValue(this.reviewPoints);
                                }
                                this.wellPoints = this.wellPoints.concat(this.reviewPoints);
                                return this.wellPoints;
                            })
                        );
                        return gisObs$;
                    }                       
                    else {
                        this.wellPoints.push({ latitude: this.currentLatitude, longitude: this.currentLongitude, type: "WellHead", wellName: "Proposed Well Head" } as AreaOfReviewPoint);
                        this.reviewPoints = this.getContextValue();
                        this.wellPoints = this.wellPoints.concat(this.reviewPoints);
                        return of(this.wellPoints)
                    }                   

                })
            ).subscribe(
                (data) => {
                    console.log("map should be loaded");
                    this.loadingPoints$.next(false);
                },
                (error) => {
                    console.error(error);
                    this.loadingPoints$.next(false);
                }
            );
            this.subscription.add(dataObs$);
        }
        else {
            this.componentData.config.rendererType = RendererTypes.AreaOfReview;
            this.componentData.config.properties = { zoom: this.defaultZoom, center: [-82.965020, 40.059260] };
        }
    }


    public getFormationName(codeString: string): string {
        let code = "Unknown Formation / Undrilled";
        let codeParts = [];
        if (!codeString || codeString.trim().length === 0) return code;
        codeString = codeString.trim();
        if (codeString.indexOf("/") !== -1) {
            codeParts = codeString.split("/");
        } else if (codeString.indexOf(",") !== -1) {
            codeParts = codeString.split(",");
        } else {
            codeParts.push(code);
        }
        let formationName = "";
        for (let f = 0; f < codeParts.length; f++) {
            const domain = this.formationDefinitions.find(d => d.Code.startsWith(code));
            const value = (domain) ? domain.Definition : code;
            if (formationName.length === 0) {
                formationName = value;
            } else if (formationName.indexOf(value) !== 0) {
                formationName = `${formationName}, ${value}`;
            }            
        }
        return formationName;
    }

    getWellType(typeCode: string): string {
        const domain = this.wellTypeDefinitions.find(d => d.Code === typeCode);
        let wellType = (domain) ? domain.Definition : typeCode;
        return wellType;
    }

    getWellTypeDescription(typeCode: string): string {
        const domain = this.wellTypeDescriptionDefinitions.find(d => d.Code === typeCode);
        let wellTypeDescription = (domain) ? domain.Definition : typeCode;
        return wellTypeDescription;
    }

    public getStatePlane(): string {
        if (this.isEdit) return StatePlane.South;
        const direction = this.wellDirection || "";
        let plane = "";
        let location = "";
        switch (direction) {
            case WellDirection.Directional:
                location = getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.StatePlaneDirectional);
                break;
            case WellDirection.OtherHorizontal:
            case WellDirection.Horizontal:
                location = getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.StatePlaneHorizontal);
                break;
            case WellDirection.Vertical:
                location = getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.StatePlaneVertical);
                break;
            default:
                break;
        }
        if (location.length > 0 && location.startsWith('$')) {
            plane = this.getContextData(location);
        }
        return plane;
    }

    public getLatitudeFromContext(): number {
        if (this.isEdit) return 0;
        const direction = this.wellDirection || "";
        switch (direction) {
            case WellDirection.Directional:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LatitudeDirectional) || ""));
            case WellDirection.Horizontal:
            case WellDirection.OtherHorizontal:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LatitudeHorizontal) || ""));
            case WellDirection.Vertical:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LatitudeVertical) || ""));
            default:
                return 0;
        }
    }

    public getLongitudeFromContext(): number {
        if (this.isEdit) return 0;
        const direction = this.wellDirection || "";
        switch (direction) {
            case WellDirection.Directional:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LongitudeDirectional) || ""));
            case WellDirection.Horizontal:
            case WellDirection.OtherHorizontal:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LongitudeHorizontal) || ""));
            case WellDirection.Vertical:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LongitudeVertical) || ""));
            default:
                return 0;
        }
    }

    public getActionRadius(): number {
        let radius = 0;
        if (!this.componentContext) return radius;
        const wellType = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.WellType));
        const wellTypeDescription = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.WellTypeDescription));
        if (wellType === this.solutionMining.type && wellTypeDescription === this.solutionMining.description) {
            radius = ActionRadius.QuarterMile;
        } else if (wellType === this.saltwaterInjection.type && wellTypeDescription === this.saltwaterInjection.description) {
            const saltwaterVolume = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.FluidVolume));
            if (saltwaterVolume > 200) {
                radius = ActionRadius.HalfMile;
            } else {
                radius = ActionRadius.QuarterMile;
            }
        } else if (wellType === this.enhancedRecovery.type && wellTypeDescription === this.enhancedRecovery.description) {
            const injectionType = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.InjectionFluidType));
            const fluidVolume = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.FluidVolume));
            if (injectionType === this.gasFluidType.Code || fluidVolume <= 200) {
                radius = ActionRadius.QuarterMile;
            } else {
                radius = ActionRadius.HalfMile;
            }
        } else {
            radius = 0;
        }
        return radius;
    }

    onMapReady(data: Renderer<esri.MapView | esri.SceneView>): void {
        super.onMapReady(data);
    }

    protected _incrementFunction(button: Button): void {
        if (this.mapRenderer && this._docService && this.doExport) {
            let export$ = this.captureImage().pipe(
                mergeMap(() => {
                    return this.captureMapToPdf();
                }),          
                finalize(() => { 
                    this.exportSubscription.unsubscribe(); 
                })).subscribe(() => {
                    this.buttonOverride.callOriginalFunction(button);
            }, (error) => {
                console.error(error);
                this.buttonOverride.callOriginalFunction(button);
            }, () => {
                this.buttonOverride.callOriginalFunction(button);
            });
            this.exportSubscription.add(export$);
        } else {
            this.buttonOverride.callOriginalFunction(button);
        }
    }

    private captureMapToPdf(): Observable<any> {
        this.setPdfMapConfig(this.componentData.config);
        return this.templateService.generateAreaOfReviewMapPdf(this.componentContext, this.componentData.config).pipe(
            mergeMap((result) => {
            const file = new Blob([result], { type: "application/pdf" }) as any;
            const fileName = `${genId()}.pdf`;
            file.name = fileName;
            file.lastModifiedDate = new Date();
            const fileToUpload = { fileName: `${fileName}`, rawFile: file } as IUploadFile;
            return this.uploadPdfFile(fileToUpload).pipe(tap((token: any) => {
                this.setUploadProperties(file, token);
                const files = this.getContext(this.location);
                if (files && files.length > 0) {
                    const index = this.getContext(this.location).findIndex(x => x.isMapCapture != null);
                    if (index > -1) {
                        files.splice(index, 1, token);
                    }
                    this.storeContext(files, this.location);
                } else {
                    this.storeContext([token], this.location);
                }
            }));
        }));
    }

    private uploadPdfFile(file: IUploadFile): Observable<IAzureBlobSasToken> {
        const uploadComplete = new Subject<IAzureBlobSasToken>();
        this._docService.provision(file.fileName).subscribe(x => {
            this.docService.processProvisionedFile(file, x, uploadComplete);
        }, (error) => {
            console.error(error);
        });
        return uploadComplete.asObservable();
    }

    private setUploadProperties(file: any, upload: any): void {
        upload.rawFile = {};
        upload.isMapCapture = true;
        upload.fileName = file.name;
        upload.completed = true;
        upload.waiting = false;
        upload.isUploading = false;
        upload.path = upload.blobUri;
        upload.percentage = "100.0";
        upload.type = file.type;
        upload.uploadedByUserId = this._authService.User.id;
        upload.uploadedByOrganizationId = this._authService.User.organizationId;
    }

    private setPdfMapConfig(config: any) {
        this.componentData.config.mapConfig = {
            "wellDataPoints": config.wellDataPoints
        };
        config.pdfDocument = this.componentData.contextTarget;
        config.pdfOptions = { useDefaults: true }; 
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }
}
